import type { MessageStatusProps, StatusConfigMap } from './index.types';
import { Icon } from 'Components/shared/Icon';
import { MobXProviderContext, observer } from 'mobx-react';
import React, { useMemo } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { Button } from '~/lib/ui/button';
import { cn } from '~/lib/utils';

const STATIC_STATUS_CONFIG_MAP: StatusConfigMap = {
  Sending: { label: 'Sending', icon: 'clock' },
  Sent: { label: 'Sent', icon: 'check' },
  Delivered: { label: 'Delivered', icon: 'double-check' },
  FailedToDeliver: {
    label: 'Delivery failed on the destination side',
    labelClassName: 'text-destructive',
    icon: 'x',
  },
  FailedToDeliverGroup: {
    label: 'Delivery failed to one or more recipients',
    labelClassName: 'text-destructive',
    icon: 'x',
  },
};

export const MessageStatus: React.FC<MessageStatusProps> = observer(
  ({ message }) => {
    const {
      conversation: { CurrentConversation },
      message: { groupedMessagesByConversationMap, retryMessage },
      person: { loggedInPersonId },
    } = React.useContext<RootStoreProps>(MobXProviderContext);

    const existingMsgGroup = groupedMessagesByConversationMap.get(
      CurrentConversation?.id
    );

    let isMostRecentMessageByUser = false;

    if (existingMsgGroup) {
      const mostRecentMessageByUser =
        existingMsgGroup.getNewestMessageByPersonId(loggedInPersonId);

      isMostRecentMessageByUser = mostRecentMessageByUser?.id === message.id;
    }

    const statusConfigMap = useMemo(
      (): StatusConfigMap => ({
        ...STATIC_STATUS_CONFIG_MAP,
        FailedToSend: {
          label: (
            <>
              Failed to send.{' '}
              <Button
                onClick={() => {
                  if (message.chat) {
                    pushToGTMDataLayer('im_message_failed_retry');
                  } else if (message.sms) {
                    pushToGTMDataLayer('sms_message_failed_retry');
                  }

                  void retryMessage(CurrentConversation, message);
                }}
                variant="link"
                size="sm"
                className="text-[10px] leading-[14px] p-0 h-auto hover:no-underline"
              >
                Click to retry
              </Button>
            </>
          ),
          icon: 'warning-circle',
        },
      }),
      [CurrentConversation, message, retryMessage]
    );

    const shouldHideStatus =
      message.isDeleted ||
      (!message.internalStatus && !message.smsStatus) ||
      ((message.smsStatus === 'Delivered' ||
        message.internalStatus === 'Sent') &&
        !isMostRecentMessageByUser);

    /**
     * If message has no status.
     * Or
     * If it's an sms message, and it's delivered or if it's a chat message, and it's sent.
     * And it's not the most recent message by the user, don't show the status.
     */
    if (shouldHideStatus) {
      return null;
    }

    const { label, icon, labelClassName } =
      statusConfigMap[message.internalStatus || message.smsStatus];

    return (
      <div className={cn('flex items-center justify-end gap-1')}>
        <Icon icon={icon} size="tiny" />
        <span className={cn('text-[10px] leading-[14px]', labelClassName)}>
          {label}
        </span>
      </div>
    );
  }
);
